import helenAvatar from '@/assets/images/echo/helen_echo.webp';
import socratesAvatar from '@/assets/images/echo/socrates_echo.webp';
import jesusAvatar from '@/assets/images/echo/jesus_echo.webp';

export interface UtmEchoPromo {
  heading: string;
  features: string[];
  cta: string;
}

export interface UtmEchoChatMessage {
  sender: 'echo' | 'user';
  type: 'text' | 'audio';
  text?: string;
}

export interface UtmEchoConfig {
  avatar: string;
  headerName: string;
  headerCategory: string;
  slug: string;
  promo: UtmEchoPromo;
  messages: UtmEchoChatMessage[];
  suggestedQuestion: string;
}

const helenConfig: UtmEchoConfig = {
  avatar: helenAvatar,
  headerName: 'Helen of Troy',
  headerCategory: 'Mythology • Ancient Greece',
  slug: 'helena-of-troy',
  promo: {
    heading: 'Hear the Story No One Let Her Tell',
    features: [
      'Explore the Bronze Age world through the eyes of one of its most famous figures',
      'Ask questions and receive thoughtful, narrative responses',
      'Discover the emotional and human side of ancient myth',
      'Experience history and legend through first-person storytelling',
    ],
    cta: 'Walk the walls of Troy.',
  },
  messages: [
    {
      sender: 'echo',
      type: 'text',
      text: 'You look at me as others once did, are you seeking a story, or do you wish to know the truth beneath it?',
    },
    { sender: 'user', type: 'text', text: 'What was your happiest moment in Troy?' },
    {
      sender: 'echo',
      type: 'text',
      text: 'My happiest moment in Troy was brief — a golden dusk when Paris and I wandered the palace gardens. For a fleeting hour, laughter outshone the looming war. Did you ever have such moments where joy felt fragile and precious?',
    },
  ],
  suggestedQuestion: 'What would you change about your story?',
};

const socratesConfig: UtmEchoConfig = {
  avatar: socratesAvatar,
  headerName: 'Socrates',
  headerCategory: 'Philosophy • Ancient Greece',
  slug: 'socrates',
  promo: {
    heading: 'Question Everything. Even Yourself.',
    features: [
      'For centuries, Socrates walked the streets of ancient Athens asking people simple questions that revealed profound truths.',
      'Now you can experience that same dialogue.',
      'Ask about life, purpose, justice, success, or your daily struggles.',
      'Let Socrates guide you through the art of thinking.',
    ],
    cta: 'Talk to Socrates!',
  },
  messages: [
    { sender: 'user', type: 'text', text: 'Greetings Socrates' },
    { sender: 'echo', type: 'audio' },
    { sender: 'user', type: 'text', text: 'What is the meaning of life?' },
    {
      sender: 'echo',
      type: 'text',
      text: 'Ah, you strike at the deepest of questions! I must admit, I do not know, yet perhaps the meaning lies in examining ourselves and questioning everything. For me, the unexamined life is not worth living. What moved you to ask such a profound question?',
    },
  ],
  suggestedQuestion: 'How do I apply Stoicism today?',
};

const jesusConfig: UtmEchoConfig = {
  avatar: jesusAvatar,
  headerName: 'Jesus',
  headerCategory: 'Spirituality • Life Coach',
  slug: 'jesus-christ',
  promo: {
    heading: 'Ask your questions. Seek wisdom. Experience timeless guidance.',
    features: [
      'Life raises difficult questions.',
      'How should I treat others?',
      'How do I forgive someone who hurt me?',
      'What does it mean to live with purpose?',
      'How can I find peace when life feels uncertain?',
    ],
    cta: 'Sometimes the most powerful answers begin with a simple question.',
  },
  messages: [
    { sender: 'user', type: 'text', text: 'Hello There' },
    { sender: 'echo', type: 'audio' },
    {
      sender: 'user',
      type: 'text',
      text: 'I feel a bit lost today, I find it hard to keep focused, what should I do?',
    },
    {
      sender: 'echo',
      type: 'text',
      text: "When you feel lost and distracted, pause for a moment and breathe deeply. Step away from the noise, find a quiet place, and be still. Sometimes, clarity comes not by striving harder, but by letting go and listening within. What usually helps you recenter when you're overwhelmed?",
    },
  ],
  suggestedQuestion: 'How do I find inner peace?',
};

const UTM_ECHO_CONFIGS = new Map<string, UtmEchoConfig>([
  ['helen', helenConfig],
  ['socrates', socratesConfig],
  ['jesus', jesusConfig],
]);

export function getUtmEchoConfig(echoKey: string | null): UtmEchoConfig {
  const key = echoKey ?? 'helen';
  return UTM_ECHO_CONFIGS.get(key) ?? getUtmEchoConfigBySlug(key) ?? helenConfig;
}

/** Lookup by URL slug (e.g. "socrates", "helen-of-troy"). Returns null if not a UTM echo. */
export function getUtmEchoConfigBySlug(slug: string): UtmEchoConfig | null {
  const byKey = UTM_ECHO_CONFIGS.get(slug);
  if (byKey) return byKey;
  for (const config of UTM_ECHO_CONFIGS.values()) {
    if (config.slug === slug) return config;
  }
  return null;
}
